/**
 * My week (3.1–3.3). The clinician's own seven days: the pattern she was handed,
 * rearranged by her, with guardrails that inform and never block.
 *
 * Everything on this page is about one clinician — whoever is signed in, which
 * the user menu switches. No other clinician's number appears anywhere on it,
 * and a card she drops lands where she dropped it before the server has
 * answered — if the server refuses, it goes back and says why.
 *
 * Both week rows are drawn by the one `DayRow` with the same props, so a card
 * reads the same in either of them and nothing on the page appears or disappears
 * when a drag starts. Next week used to render `compact` at rest and expand
 * mid-drag, which hid each visit's type — its subtitle — until she picked a card
 * up. The collapse bought one caption line per column; the flip cost the page its
 * word for what a visit is, so the collapse went.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, IconButton, Paper, Snackbar, Stack, Tooltip, Typography } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Schedule from "@mui/icons-material/Schedule";
import AddCalendarBlock from "~/components/command/AddCalendarBlock";
import CancelVisitDialog from "~/components/command/CancelVisitDialog";
import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import { CalendarBlockBlockTypeEnum } from "~/graphql/__generated__/graphql";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { useNow } from "../../components/useNow";
import { addDaysToKey, addWorkingDaysToKey, dateKeyAt, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { MAX_DELAY_HOURS, roundFired, roundFor, roundSendTime, selectConfirmationRows } from "../../engine/confirmationRound";
import { dialogReasons, selectDayMarks, type DayMark as DayMarkModel, type MarkTarget } from "../../engine/dayMarks";
import { evaluateRealVisitMove, type RealMoveEvaluation } from "../../engine/moveGuard";
import { selectMyWeek, type MyWeekDay, type WeekCard } from "../../engine/myWeek";
import type { WeekPoints } from "../../engine/utilization";
import { confirmByPhoneNow, delayConfirmationRound, moveVisitOptimistically, recordBlockCreated, recordVisitCanceled, removeBlockFromWeek } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import type { DateKey, RoundStatus } from "@caredna-marlin/utils/homeHealthDemo/state";
import type { TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";
import { ConfirmationIcon } from "../../components/ConfirmationIcon";
import { useBranchClock } from "../../components/branchClock";
import { useCardWindowLabel } from "../../components/useCardWindowLabel";
import { CardMenu } from "./CardMenu";
import { DayMenu } from "./DayMenu";
import { DayMark } from "./DayMark";
import { MoveAnywayDialog } from "./MoveAnywayDialog";
import { MyDayDialog } from "./MyDayPage";
import { RouteDayDialog } from "./RouteDayDialog";
import { SequenceDayDialog } from "./SequenceDayDialog";
import { isInspectable, VisitInspectPopover } from "./VisitInspectPopover";

interface Dragging {
  realVisitId: number;
  patientName: string;
  fromDate: DateKey;
}

interface PendingMove {
  realVisitId: number;
  patientName: string;
  toDate: DateKey;
  evaluation: RealMoveEvaluation;
  /** What the dialog lists: the closed-list text, the day mark's reasons included. */
  reasons: TimeSentence[];
}

export function MyWeekPage() {
  const state = useDemoState();
  const navigate = useNavigate();
  const now = useNow(30_000);
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const today = toDateKey(selectDemoNow(state, now), tz);
  // Whose week this is comes from the signed-in clinician, not from the URL:
  // the user menu is the one control that changes it.
  const providerId = useCurrentOrg()?.providerId ?? undefined;
  const bindings = state.meta.bindings.providerIdsByHandle;
  const bound = Object.keys(bindings).length > 0;
  const handle = useMemo(() => Object.entries(bindings).find(([, id]) => id === providerId)?.[0] ?? "", [bindings, providerId]);
  const clinician = state.roster[handle];

  /**
   * My day opens **over** this page rather than instead of it (Pete, 2026-09-19),
   * but the date stays in the URL: `/my-week/:date?` is one route, so the week
   * behind the panel is not remounted when a day opens and the browser's back
   * button is still the way out of an opened day. A deep link still lands on the
   * day, and anything that is not a date is simply no day at all.
   */
  const { date: openDateParam } = useParams();
  const openDate: DateKey | undefined = /^\d{4}-\d{2}-\d{2}$/.test(openDateParam ?? "") ? (openDateParam as DateKey) : undefined;

  const [anchor, setAnchor] = useState<DateKey>(() => today);
  const week = useMemo(() => selectMyWeek(state, handle, anchor, now), [state, handle, anchor, now]);
  const nextWeek = useMemo(() => selectMyWeek(state, handle, addDaysToKey(week.weekStart, 7), now), [state, handle, week.weekStart, now]);

  const [dragging, setDragging] = useState<Dragging>();
  const [hover, setHover] = useState<{ date: DateKey; evaluation: RealMoveEvaluation }>();
  const [pendingMove, setPendingMove] = useState<PendingMove>();
  const [blockFor, setBlockFor] = useState<{ day: MyWeekDay; anchor: HTMLElement }>();
  const [cancelFor, setCancelFor] = useState<WeekCard>();
  const [sequenceFor, setSequenceFor] = useState<MyWeekDay>();
  const [routeFor, setRouteFor] = useState<MyWeekDay>();
  const [message, setMessage] = useState<string>();
  /**
   * The card a single click has selected (3.2, look before you move): its
   * popover is open, anchored to the tile. Picking up any card closes it.
   */
  const [inspected, setInspected] = useState<{ card: WeekCard; anchor: HTMLElement }>();
  const closeInspect = useCallback(() => setInspected(undefined), []);
  const onSelectCard = useCallback((card: WeekCard, anchor: HTMLElement) => {
    setInspected((current) => (current?.card.id === card.id ? undefined : { card, anchor }));
  }, []);

  /**
   * **Day marks** (3.2 stage 2): while a visit is selected or in hand, every day
   * header of both weeks says whether it could go there. The visit in hand wins
   * over the selected one — picking a card up closes the popover, and the marks
   * stay lit for the card being dragged until it is let go. Recomputed from the
   * store, so a drop or a Save is reflected the moment the week is.
   */
  const markTarget: MarkTarget | undefined = dragging
    ? { realVisitId: dragging.realVisitId }
    : inspected?.card.realVisitId !== undefined
      ? { realVisitId: inspected.card.realVisitId }
      : inspected?.card.planVisitId !== undefined
        ? { planVisitId: inspected.card.planVisitId }
        : undefined;
  const markKey = markTarget ? ("realVisitId" in markTarget ? `visit-${markTarget.realVisitId}` : `plan-${markTarget.planVisitId}`) : undefined;
  const marks = useMemo(() => {
    if (!markTarget) return undefined;
    return selectDayMarks(state, markTarget, handle, [...week.days, ...nextWeek.days].map((d) => d.date), now);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `markKey` stands for the target object
  }, [state, markKey, handle, week.days, nextWeek.days, now]);
  /**
   * A drag must not end in an opened day. Browsers do not fire `click` after a
   * drag, but nothing in this page depends on that promise: the flag is set the
   * moment a card is picked up and cleared a tick after the drag ends, which is
   * after any release event a browser might still deliver.
   */
  const draggedRecently = useRef(false);

  const performMove = useCallback(
    async (realVisitId: number, toDate: DateKey) => {
      const vr = state.server.visitResources.find((v) => v.visitId === realVisitId);
      if (!vr) return;
      const hhmm = formatInBranchZone(new Date(vr.startIso), tz, "HH:mm");
      const start = dateKeyAt(toDate, hhmm, tz);
      const end = new Date(start.getTime() + Math.max(15, vr.durationMinutes) * 60_000);
      try {
        await moveVisitOptimistically(realVisitId, { startIso: start.toISOString(), endIso: end.toISOString() });
        setMessage(`${vr.patientName} moved to ${clock.dateTime(start)}`);
      } catch (error) {
        setMessage(`Could not move the visit — ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [state, tz, clock, week.days, nextWeek.days],
  );

  /**
   * The product's dialog holds the reason list and runs the cancel itself, so
   * the demo picks up afterwards: the card comes off the day before the horizon
   * poll has said anything. A cancel that
   * the server refuses never reaches here — nothing was removed, so there is
   * nothing to put back.
   */
  const onVisitCanceled = useCallback(async (card: WeekCard) => {
    if (card.realVisitId === undefined) return;
    try {
      await recordVisitCanceled(card.realVisitId);
      setMessage(`${card.title} canceled`);
    } catch (error) {
      setMessage(`Could not read the cancel back — ${error instanceof Error ? error.message : String(error)}`);
    }
  }, []);

  const performRemoveBlock = useCallback(async (card: WeekCard) => {
    if (card.blockId === undefined) return;
    try {
      await removeBlockFromWeek(card.blockId);
      setMessage(`${card.title} removed`);
    } catch (error) {
      setMessage(`Could not remove the block — ${error instanceof Error ? error.message : String(error)}`);
    }
  }, []);

  /** **Mark confirmed** (3.3): she called the patient, so the visit is confirmed and the reasons go with the call. */
  const markConfirmed = useCallback((card: WeekCard) => {
    if (card.realVisitId === undefined) return;
    confirmByPhoneNow(card.realVisitId);
    setMessage(`${card.title} confirmed`);
  }, []);

  const onDropOn = (date: DateKey) => {
    const drag = dragging;
    setDragging(undefined);
    setHover(undefined);
    endDrag();
    if (!drag || date === drag.fromDate) return;
    const evaluation = evaluateRealVisitMove(state, drag.realVisitId, date, undefined, now);
    if (evaluation.level === "block") return;
    // An amber day asks, whichever said so: the guards, or the day mark's own
    // check (a second visit that day), so the outline, the mark and the drop agree.
    const mark = marks?.[date];
    if (evaluation.level === "warn" || mark?.kind === "caution") {
      const reasons = dialogReasons(evaluation);
      for (const reason of mark?.reasons ?? []) if (!reasons.includes(reason)) reasons.push(reason);
      setPendingMove({ realVisitId: drag.realVisitId, patientName: drag.patientName, toDate: date, evaluation, reasons });
      return;
    }
    void performMove(drag.realVisitId, date);
  };

  /**
   * A day opens by clicking anywhere on its column, and the column is also the
   * drop target for a cross-day drag, so three things have to be told apart:
   *
   * - **A card, and the ⋯ menus.** Both are `closest()` lookups off the click's
   *   own target — `[data-card-kind]` is the card tile and `button` covers the
   *   day menu's trigger, the card menu's trigger and the round's header line,
   *   which opens the day itself. An open menu is a **React portal**: its DOM
   *   sits at the document root but a click in it still propagates up the React
   *   tree, through this column, so the first thing checked is that the target
   *   is really inside the column's own DOM.
   * - **The end of a drag**, by `draggedRecently`, which both ends of a drag
   *   clear: a successful drop clears it in `onDropOn`, a cancelled one in
   *   `onDragEnd`. It needs both. An optimistic drop re-renders the week before
   *   the browser gets to `dragend`, and the card it fires on has by then been
   *   unmounted from the column it left — a detached node's event never reaches
   *   the React root, so `dragend` alone would leave the flag set for the rest
   *   of the act and swallow every column click after the first drag.
   * - **A day already spent**, the same rule the day menu follows: a past column
   *   carries no menu and does not open.
   */
  const onDayClick = (day: MyWeekDay, event: ReactMouseEvent<HTMLElement>) => {
    if (day.isPast || draggedRecently.current) return;
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return;
    if (target.closest("[data-card-kind]") || target.closest("button")) return;
    navigate(`/my-week/${day.date}`);
  };

  const onCardDragStart = (drag: Dragging) => {
    draggedRecently.current = true;
    setInspected(undefined);
    setDragging(drag);
  };

  const onDragOverDay = (date: DateKey) => {
    if (!dragging) return;
    if (hover?.date === date) return;
    setHover({ date, evaluation: evaluateRealVisitMove(state, dragging.realVisitId, date, undefined, now) });
  };

  /**
   * Dragged off the columns — into the gap between two, onto a heading — the day
   * is no longer the target, so its outline and its popper go. A drag event over
   * a column bubbles up here too, which is why the target is checked.
   */
  const onDragOverPage = (event: ReactDragEvent<HTMLElement>) => {
    if (!dragging || !hover) return;
    if (!(event.target as HTMLElement).closest("[data-day]")) setHover(undefined);
  };

  /**
   * The day's popper opens by itself during a drag once the cursor has rested on
   * a day for a moment, and closes the moment it moves on, leaves, or lets go.
   */
  const [restDate, setRestDate] = useState<DateKey>();
  const hoverDate = dragging ? hover?.date : undefined;
  useEffect(() => {
    setRestDate(undefined);
    if (!hoverDate) return;
    const timer = setTimeout(() => setRestDate(hoverDate), DRAG_REST_MS);
    return () => clearTimeout(timer);
  }, [hoverDate]);

  // `drop` only fires on a day column, so a card released anywhere else — the gap
  // between two columns, a heading, Escape — used to leave the drag state set and
  // the strip on screen for the rest of the act. `dragend` fires on the source
  // after any release the source is still there for, which is every cancelled
  // drag; a *successful* drop unmounts the card as the week re-renders around
  // the optimistic move, and an event on a detached node never reaches the React
  // root. So the two together are the honest end, and `onDropOn` clears the same
  // state this does.
  const onDragEnd = () => {
    setDragging(undefined);
    setHover(undefined);
    endDrag();
  };

  /** A tick later, so a `click` a browser still delivers for this release is suppressed and the next genuine one is not. */
  function endDrag() {
    setTimeout(() => {
      draggedRecently.current = false;
    }, 0);
  }

  const onBlockCreated = (blockId: number, block: Parameters<typeof recordBlockCreated>[1]) => {
    void recordBlockCreated(blockId, block).catch((error: unknown) => setMessage(`Could not read the new block back — ${error instanceof Error ? error.message : String(error)}`));
  };

  // "Tomorrow" is the next working day, so on a Friday the round is about Monday.
  // The header says which day it is about; the week's own columns say nothing.
  const tomorrow = addWorkingDaysToKey(today, 1);
  /**
   * The confirmation round (3.3), in the week's own header — one affordance,
   * before and after (Pete, 2026-09-19: *"confirmations sent at / delay stuff
   * should move out to the root week view… annoying to have to find and click
   * into tomorrow to do something you're likely in a hurry to do"*).
   *
   * Before the round it is the time and a click that pushes it back, an hour at a
   * time up to two, for this clinician's round today only. After it, the same
   * spot carries what came back. It sits beside the clinician's name rather than
   * on a day column, so scrolling the week or changing the anchor never takes it
   * off screen — and it is the **only** place the round is worded, which is why
   * the column's one-line pointer is gone.
   */
  const roundSummary = useMemo(() => {
    const round = roundFor(state, providerId, tomorrow);
    const dayName = formatInBranchZone(new Date(`${tomorrow}T12:00:00Z`), "UTC", "EEEE");
    if (!roundFired(round)) {
      return {
        label: `Confirmations sent at ${clock.wall(roundSendTime(state, round))}`,
        tooltip: `${dayName}'s visits · click to delay by an hour${round.delayHours >= MAX_DELAY_HOURS ? " (already delayed two hours)" : ""}`,
        canDelay: round.delayHours < MAX_DELAY_HOURS,
      };
    }
    const rows = selectConfirmationRows(state, providerId, tomorrow);
    /**
     * **What the round got back: a yes, something else, or nothing** (Pete,
     * 2026-09-22, amended 2026-09-24). The three terms are replies, not jobs,
     * and zeros are printed rather than hidden — a missing term would read as a
     * state that does not exist rather than one nobody is in.
     *
     * The middle term used to be *declined*, which the round is no longer
     * willing to claim it can tell from a request for another time. It counts
     * the same patients it always did: the ones who answered with something
     * that was not a yes, which is a **Needs a call** row **carrying their
     * words**. The two ways into Needs a call that are not about a reply at all
     * — a visit moved after its text, a patient with no text consent — stay out
     * of all three, as before. The card and the worklist still carry them.
     */
    const count = (s: RoundStatus) => rows.filter((r) => r.status === s).length;
    const answered = rows.filter((r) => r.status === "needsCall" && r.reasons.some((x) => x.kind === "words")).length;
    return {
      label: `${count("confirmed")} confirmed · ${answered} need a call · ${count("pending")} no response`,
      tooltip: `${dayName}'s visits · the texts have gone`,
      canDelay: false,
    };
  }, [state, providerId, tomorrow, clock]);

  // Until the horizon read has resolved the roster there is nothing to say yet;
  // once it has, a clinician the branch does not staff gets told so plainly.
  if (!bound) return <Box sx={{ p: 3 }} />;
  if (!clinician || providerId === undefined) {
    return (
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          My week
        </Typography>
        <Alert severity="info" variant="outlined">
          My week shows the week of the clinician you are signed in as. This sign-in is not one of the branch&apos;s clinicians — choose one from the user menu at the top right.
        </Alert>
      </Box>
    );
  }

  return (
    <Box onDragEnd={onDragEnd} onDragOver={onDragOverPage} sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Pete, 2026-09-19: the round belongs at the **top right** of the page, and
          the clinician's name comes off it altogether — whose week this is, is
          already answered by the user menu that chose them. So the left of the
          header is the title and the week's own controls, and the round sits
          alone on the right where a thing you reach for against a clock should
          be. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexWrap: "wrap" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>
            My week
          </Typography>
          <Stack direction="row" alignItems="center">
            <IconButton size="small" aria-label="Previous week" onClick={() => setAnchor(addDaysToKey(week.weekStart, -7))}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Button size="small" onClick={() => setAnchor(today)}>
              Today
            </Button>
            <IconButton size="small" aria-label="Next week" onClick={() => setAnchor(addDaysToKey(week.weekStart, 7))}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* The round, before and after, in one place. A click delays it while there
            is still a round to delay; afterwards it is the outcome and nothing to
            press — the counts stay here so a click could one day open them. */}
        <Tooltip title={roundSummary.tooltip}>
          {roundSummary.canDelay ? (
            <Button
              size="small"
              color="inherit"
              startIcon={<Schedule fontSize="small" />}
              onClick={() => delayConfirmationRound(handle, tomorrow)}
              data-testid="round-summary"
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              {roundSummary.label}
            </Button>
          ) : (
            // Once the round has gone — or once it has been pushed back as far as it
            // goes — this is information rather than a control. A *disabled* button
            // would keep the counts out of the tab order and announce them as
            // unavailable; a span reads them out and is still something a click could
            // one day be attached to.
            <Stack direction="row" alignItems="center" spacing={0.5} data-testid="round-summary" sx={{ color: "text.secondary" }}>
              <Schedule fontSize="small" />
              <Typography variant="body2">{roundSummary.label}</Typography>
            </Stack>
          )}
        </Tooltip>
      </Stack>

      {/* No strip on My week (Pete, 2026-10-08): the day under the card outlines green
          or amber, and its popper says why. My day keeps its strip. */}
      <Box>
        <WeekHeading label="This week" points={week.points} />
        <DayRow
          days={week.days}
          clinicianName={clinician?.displayName ?? handle}
          onDragStart={onCardDragStart}
          onDragOverDay={onDragOverDay}
          onDrop={onDropOn}
          hoverDate={hover?.date}
          hoverLevel={hover && hover.evaluation.level === "ok" && marks?.[hover.date]?.kind === "caution" ? "warn" : hover?.evaluation.level}
          onAddBlock={(day, anchor) => setBlockFor({ day, anchor })}
          onSequence={setSequenceFor}
          onViewRoute={setRouteFor}
          onOpenDay={(day) => navigate(`/my-week/${day.date}`)}
          onDayClick={onDayClick}
          onCancelVisit={setCancelFor}
          onRemoveBlock={performRemoveBlock}
          onMarkConfirmed={markConfirmed}
          onSelectCard={onSelectCard}
          selectedCardId={inspected?.card.id}
          marks={marks}
          restDate={restDate}
          timeZone={tz}
          tomorrow={tomorrow}
        />
      </Box>

      <Box>
        <WeekHeading label="Next week" points={nextWeek.points} />
        <DayRow
          days={nextWeek.days}
          clinicianName={clinician?.displayName ?? handle}
          onDragStart={onCardDragStart}
          onDragOverDay={onDragOverDay}
          onDrop={onDropOn}
          hoverDate={hover?.date}
          hoverLevel={hover && hover.evaluation.level === "ok" && marks?.[hover.date]?.kind === "caution" ? "warn" : hover?.evaluation.level}
          onAddBlock={(day, anchor) => setBlockFor({ day, anchor })}
          onSequence={setSequenceFor}
          onViewRoute={setRouteFor}
          onOpenDay={(day) => navigate(`/my-week/${day.date}`)}
          onDayClick={onDayClick}
          onCancelVisit={setCancelFor}
          onRemoveBlock={performRemoveBlock}
          onMarkConfirmed={markConfirmed}
          onSelectCard={onSelectCard}
          selectedCardId={inspected?.card.id}
          marks={marks}
          restDate={restDate}
          timeZone={tz}
          tomorrow={tomorrow}
        />
      </Box>

      {pendingMove && (
        <MoveAnywayDialog
          patientName={pendingMove.patientName}
          toLabel={formatInBranchZone(new Date(`${pendingMove.toDate}T12:00:00Z`), "UTC", "EEE MMM d")}
          evaluation={pendingMove.evaluation}
          reasons={pendingMove.reasons}
          onKeep={() => setPendingMove(undefined)}
          onMoveAnyway={() => {
            const move = pendingMove;
            setPendingMove(undefined);
            void performMove(move.realVisitId, move.toDate);
          }}
        />
      )}
      {cancelFor?.realVisitId !== undefined && (
        <CancelVisitDialog
          visitId={cancelFor.realVisitId}
          onClose={() => setCancelFor(undefined)}
          onSuccess={() => {
            const canceled = cancelFor;
            setCancelFor(undefined);
            void onVisitCanceled(canceled);
          }}
        />
      )}
      <CalendarPopup isOpen={!!blockFor} visitAnchor={blockFor?.anchor} onClose={() => setBlockFor(undefined)}>
        {blockFor && (
          <AddCalendarBlock
            key={blockFor.day.date}
            date={dateKeyAt(blockFor.day.date, "12:00", tz)}
            blockType={CalendarBlockBlockTypeEnum.Meeting}
            lockedProviderId={providerId}
            onCreated={onBlockCreated}
            onClose={() => setBlockFor(undefined)}
          />
        )}
      </CalendarPopup>
      {sequenceFor && (
        <SequenceDayDialog date={sequenceFor.date} providerId={providerId} onClose={() => setSequenceFor(undefined)} />
      )}
      {/* Mounted only while she is looking at it, so the route view's location poll stops when she closes it. */}
      {routeFor && (
        <RouteDayDialog
          date={routeFor.date}
          providerId={providerId}
          providerName={clinician?.displayName ?? handle}
          onClose={() => setRouteFor(undefined)}
        />
      )}
      {inspected && (
        <VisitInspectPopover card={inspected.card} anchorEl={inspected.anchor} clinicianName={clinician?.displayName ?? handle} onClose={closeInspect} />
      )}
      {openDate && <MyDayDialog date={openDate} onClose={() => navigate("/my-week")} />}
      <Snackbar open={!!message} autoHideDuration={6000} onClose={() => setMessage(undefined)} message={message} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}

/** How long a drag rests on a day before its popper opens by itself. */
const DRAG_REST_MS = 400;

/**
 * **points** and **reserved** are the words every screen uses now (the 2026-09-18
 * vocabulary sweep took them to the heat map, the strip, the referral panel and
 * the phone). This is the only place that composes them from `selectWeekPoints`
 * in this file; the heat-map hover and the utilization strip compose their own
 * from the same selector, so the three screens cannot disagree about a value.
 *
 * `WeekHeading` is its only caller. A chip in the top right of the page used to
 * print this week's triple a second time; each week's heading is the one place a
 * week's total appears now.
 */
function weekAgainstTarget(points: WeekPoints): string {
  return `${points.firm} points · ${points.provisional} reserved · target ${points.target}`;
}

/** One heading for both rows, so "This week" and "Next week" cannot drift apart. */
function WeekHeading({ label, points }: { label: string; points: WeekPoints }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {weekAgainstTarget(points)}
      </Typography>
    </Stack>
  );
}

function DayRow({
  days,
  clinicianName,
  hoverDate,
  hoverLevel,
  onDragStart,
  onDragOverDay,
  onDrop,
  onAddBlock,
  onSequence,
  onViewRoute,
  onOpenDay,
  onDayClick,
  onCancelVisit,
  onRemoveBlock,
  onMarkConfirmed,
  onSelectCard,
  selectedCardId,
  marks,
  restDate,
  timeZone,
  tomorrow,
}: {
  days: MyWeekDay[];
  clinicianName: string;
  hoverDate?: DateKey;
  hoverLevel?: RealMoveEvaluation["level"];
  onDragStart: (dragging: Dragging) => void;
  onDragOverDay: (date: DateKey) => void;
  onDrop: (date: DateKey) => void;
  onAddBlock: (day: MyWeekDay, anchor: HTMLElement) => void;
  onSequence: (day: MyWeekDay) => void;
  onViewRoute: (day: MyWeekDay) => void;
  onOpenDay: (day: MyWeekDay) => void;
  /** The column itself opens the day; the page decides what a click on it means. */
  onDayClick: (day: MyWeekDay, event: ReactMouseEvent<HTMLElement>) => void;
  onCancelVisit: (card: WeekCard) => void;
  onRemoveBlock: (card: WeekCard) => void;
  /** **Mark confirmed**: the clinician called the patient herself. */
  onMarkConfirmed: (card: WeekCard) => void;
  /** A single click on a visit card opens its popover (3.2). */
  onSelectCard: (card: WeekCard, anchor: HTMLElement) => void;
  selectedCardId?: string;
  /** The day marks for the visit selected or in hand, by date. */
  marks?: Record<DateKey, DayMarkModel>;
  /** The day a drag has rested on long enough to open its popper. */
  restDate?: DateKey;
  timeZone: string;
  /** The next working day — the one the confirmation round is about. */
  tomorrow: DateKey;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "stretch", overflowX: "auto" }}>
      {days.map((day) => {
        // No red anywhere: a day the move cannot land on simply takes no outline.
        const isHover = hoverDate === day.date && hoverLevel !== "block";
        return (
          <Paper
            key={day.date}
            variant="outlined"
            data-day={day.date}
            onDragOver={(e) => {
              e.preventDefault();
              onDragOverDay(day.date);
            }}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(day.date);
            }}
            onClick={(e) => onDayClick(day, e)}
            sx={{
              flex: 1,
              minWidth: 170,
              p: 1,
              cursor: day.isPast ? "default" : "pointer",
              opacity: day.isPast ? 0.6 : 1,
              borderColor: isHover ? (hoverLevel === "warn" ? "warning.main" : "success.main") : undefined,
              borderWidth: isHover ? 2 : 1,
              bgcolor: day.isToday ? "action.hover" : undefined,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} sx={{ minHeight: 30, mb: 0.5 }}>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {day.weekdayLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {day.dateLabel}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.25}>
                {marks?.[day.date] && <DayMark mark={marks[day.date]} dragOpen={restDate === day.date} />}
                {/* A day she can still change carries its own menu; a day already spent carries none. */}
                {!day.isPast && (
                  <DayMenu
                    label={`${day.weekdayLabel} ${day.dateLabel}`}
                    // A day with one stop has no order to change, so nothing is priced for it.
                    canSequence={day.cards.filter((c) => c.kind === "visit").length > 1}
                    onOpenDay={() => onOpenDay(day)}
                    onAddBlock={(anchor) => onAddBlock(day, anchor)}
                    onSequence={() => onSequence(day)}
                    onViewRoute={() => onViewRoute(day)}
                  />
                )}
              </Stack>
            </Stack>
            <Stack spacing={0.75}>
              {day.cards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  clinicianName={clinicianName}
                  dayLabel={`${day.weekdayLabel} ${day.dateLabel}`}
                  onDragStart={onDragStart}
                  onCancelVisit={onCancelVisit}
                  onRemoveBlock={onRemoveBlock}
                  onMarkConfirmed={onMarkConfirmed}
                  onSelect={onSelectCard}
                  selected={selectedCardId === card.id}
                  timeZone={timeZone}
                />
              ))}
              {day.cards.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {/* The total, and only the total (Pete, 2026-09-19: *"the drive home
                  should just be included in the total drive time. No need to call
                  it out separately"*). It always did include the drive home —
                  `startOfShift + interVisit + endOfShift`. What was in brackets was
                  the change a drag had just made, which now shows as the total
                  moving rather than as a second figure beside it. */}
              {day.points} points · drive {day.driveMinutes} min
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );
}

function CardTile({
  card,
  clinicianName,
  dayLabel,
  onDragStart,
  onCancelVisit,
  onRemoveBlock,
  onMarkConfirmed,
  onSelect,
  selected,
  timeZone,
}: {
  card: WeekCard;
  clinicianName: string;
  dayLabel: string;
  /** The branch's zone: the window is the patient's local hour, not the laptop's. */
  timeZone: string;
  onDragStart: (dragging: Dragging) => void;
  onCancelVisit: (card: WeekCard) => void;
  onRemoveBlock: (card: WeekCard) => void;
  /** **Mark confirmed**: the clinician called the patient herself. */
  onMarkConfirmed: (card: WeekCard) => void;
  onSelect: (card: WeekCard, anchor: HTMLElement) => void;
  selected: boolean;
}) {
  const cardWindow = useCardWindowLabel(timeZone);
  const clock = useBranchClock(timeZone);
  // A card is dragged by the whole tile, so the one thing that must not start a
  // drag is the corner she reaches for to open the menu: while the pointer is
  // over it the tile is not draggable at all, and a `dragstart` that reaches the
  // corner anyway is stopped there. The menu itself is a portal, so nothing it
  // renders is inside the tile the page listens to for a drag.
  const [overMenu, setOverMenu] = useState(false);
  // Both placeholders are hatched — each occupies a slot and cedes it to a real
  // visit. The edge is what tells them apart: a reserved slot and a held one are
  // dashed, because the week may yet give them up; a planned visit is authorized
  // and waiting only for a day, so it takes a solid edge in the page's own
  // primary colour.
  const inspectable = isInspectable(card);
  const planned = card.kind === "planned";
  const hatch = card.hatched
    ? {
        backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0 6px, transparent 6px 12px)",
        borderStyle: planned ? ("solid" as const) : ("dashed" as const),
        ...(planned ? { borderColor: "primary.main" } : {}),
      }
    : {};
  return (
    <Tooltip title={card.kind === "hold" ? `Held for a start of care on ${clinicianName}` : card.address ?? ""} disableHoverListener={card.kind !== "hold" && !card.address}>
      <Paper
        variant="outlined"
        draggable={card.draggable && !overMenu}
        onDragStart={() => card.realVisitId !== undefined && onDragStart({ realVisitId: card.realVisitId, patientName: card.title, fromDate: card.date })}
        // A single click selects a visit and opens its popover. The card's own menu
        // is a button and a portal, so a click meant for it — or for anything the
        // menu renders, which still bubbles through the React tree — is not one.
        onClick={(e) => {
          if (!inspectable) return;
          const target = e.target as HTMLElement;
          if (!e.currentTarget.contains(target) || target.closest("button")) return;
          onSelect(card, e.currentTarget);
        }}
        sx={{
          p: 0.75,
          cursor: card.draggable ? "grab" : inspectable ? "pointer" : "default",
          ...(selected ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 } : {}),
          bgcolor: card.kind === "block" ? "grey.100" : undefined,
          opacity: card.pending ? 0.7 : 1,
          ...hatch,
        }}
        data-card-kind={card.kind}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
          {/* The window the patient has, not the minute the clinician plans on
              (Pete, 2026-09-19); a card with no window reads its own span. Both
              are phrased through the product's own helpers, so both follow the
              user's twelve/twenty-four-hour setting. */}
          <Typography variant="caption" color="text.secondary" component="div">
            {cardWindow(card)}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
            {card.roundStatus && <ConfirmationIcon status={card.roundStatus} label={card.roundLabel && clock.sentence(card.roundLabel)} />}
            {(card.removable || card.markConfirmable) && (
              <Box
                onMouseEnter={() => setOverMenu(true)}
                onMouseLeave={() => setOverMenu(false)}
                onDragStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <CardMenu card={card} dayLabel={dayLabel} timeZone={timeZone} onCancelVisit={onCancelVisit} onRemoveBlock={onRemoveBlock} onMarkConfirmed={onMarkConfirmed} />
              </Box>
            )}
          </Stack>
        </Stack>
        <Typography variant="body2" fontWeight={500} component="div">
          {card.title}
        </Typography>
        {card.subtitle && (
          <Typography variant="caption" color="text.secondary" component="div">
            {card.subtitle}
          </Typography>
        )}
        {card.address && (
          <Typography variant="caption" color="text.secondary" component="div">
            {card.address}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
}
